import { useState, useEffect, useRef } from "react";
import { workers } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Phone, MapPin, Pencil, Upload, FileSpreadsheet, AlertTriangle, CreditCard, Fingerprint, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import * as XLSX from "xlsx";

export default function LabourManager() {
  const [workerList, setWorkerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", role: "Labourer", daily_wage: "", advance: "0", site_assigned: "", phone: "", aadhaar_number: "", bank_account: "", ifsc_code: "" });
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const importFileRef = useRef(null);

  const today = new Date().toISOString().split("T")[0];

  const fetchWorkers = async () => {
    try {
      const r = await workers.list();
      setWorkerList(r.data);
    } catch (err) {
      toast.error("Failed to load workers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWorkers(); }, []);

  const openEdit = (w) => {
    setEditId(w.id);
    setForm({ name: w.name, role: w.role, daily_wage: String(w.daily_wage), advance: String(w.advance), site_assigned: w.site_assigned, phone: w.phone, aadhaar_number: w.aadhaar_number || "", bank_account: w.bank_account || "", ifsc_code: w.ifsc_code || "" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, daily_wage: parseFloat(form.daily_wage) || 0, advance: parseFloat(form.advance) || 0 };
    try {
      if (editId) {
        await workers.update(editId, payload);
        toast.success("Worker updated");
      } else {
        await workers.create(payload);
        toast.success("Worker added");
      }
      setDialogOpen(false);
      setEditId(null);
      setForm({ name: "", role: "Labourer", daily_wage: "", advance: "0", site_assigned: "", phone: "", aadhaar_number: "", bank_account: "", ifsc_code: "" });
      fetchWorkers();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Upgrade to Professional — WhatsApp us at +91 98765 43210", { duration: 6000 });
      } else {
        toast.error("Failed to save");
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await workers.remove(id);
      toast.success("Worker removed");
      fetchWorkers();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleAttendance = async (workerId, present) => {
    try {
      await workers.attendance(workerId, { date: today, present });
      fetchWorkers();
    } catch (err) {
      toast.error("Failed to update attendance");
    }
  };

  // ---- Bulk Import ----
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportPreview(null);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        const r = await workers.bulkImport({ file_data: base64, file_type: file.type });
        if (r.data.success) {
          setImportPreview(r.data);
        } else {
          toast.error(r.data.error || "Failed to parse file");
        }
        setImportLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Upgrade to Professional — WhatsApp us at +91 98765 43210", { duration: 6000 });
      } else {
        toast.error("Import failed");
      }
      setImportLoading(false);
    }
  };

  const handleImportDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && importFileRef.current) {
      const dt = new DataTransfer();
      dt.items.add(file);
      importFileRef.current.files = dt.files;
      handleImportFile({ target: { files: dt.files } });
    }
  };

  const confirmImport = async () => {
    if (!importPreview?.workers) return;
    setConfirmLoading(true);
    try {
      const validWorkers = importPreview.workers.filter(w => w.name).map(w => ({
        name: w.name,
        phone: w.phone || "",
        aadhaar_number: w.aadhaar_number || "",
        bank_account: w.bank_account || "",
        ifsc_code: w.ifsc_code || "",
        daily_wage: w.daily_wage || 0,
        site_assigned: w.site_assigned || "",
        role: w.role || "Labourer",
        advance: 0,
      }));
      const r = await workers.bulkConfirm(validWorkers);
      toast.success(`${r.data.imported} workers imported!`);
      setImportDialogOpen(false);
      setImportPreview(null);
      fetchWorkers();
    } catch (err) {
      toast.error("Import failed");
    } finally {
      setConfirmLoading(false);
    }
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const data = [
      ["Name", "Phone", "Aadhaar Number", "Bank Account Number", "IFSC Code", "Daily Wage", "Site Assigned", "Role/Trade"],
      ["Rajesh Kumar", "9876543210", "1234-5678-9012", "12345678901234", "SBIN0001234", 800, "Site A", "Mason"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Workers");
    XLSX.writeFile(wb, "worker_import_template.xlsx");
  };

  const roles = ["Labourer", "Mason", "Carpenter", "Electrician", "Plumber", "Supervisor", "Driver", "Helper"];
  const inputCls = "w-full bg-[#0A0A08] border border-[#27272A] text-[#F5F2E8] px-3 py-2.5 text-sm focus:border-[#FF5C1A] focus:ring-1 focus:ring-[#FF5C1A] outline-none transition-colors placeholder:text-[#444]";
  const presentCount = workerList.filter((w) => w.attendance?.[today] === true).length;

  return (
    <div className="space-y-6" data-testid="labour-manager">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl tracking-[2px]">LABOUR MANAGER</h2>
          <p className="text-sm text-[#888880] mt-1">
            <span className="font-mono text-[#2ECC71]">{presentCount}</span> / {workerList.length} present today
          </p>
        </div>
        <div className="flex gap-2">
          {/* Bulk Import */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <button data-testid="import-workers-btn" className="bg-[#111110] border border-[#27272A] text-[#F5F2E8] px-4 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:border-[#FF5C1A] transition-colors flex items-center gap-2">
                <FileSpreadsheet size={14} /> Import Excel
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-[2px]">IMPORT WORKERS FROM EXCEL</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <button data-testid="download-template-btn" onClick={downloadTemplate} className="text-[#FF5C1A] font-mono text-xs tracking-widest uppercase hover:text-[#FFB800] transition-colors underline">
                  Download Template (.xlsx)
                </button>

                <div
                  data-testid="import-drop-zone"
                  onDrop={handleImportDrop}
                  onDragOver={(e) => e.preventDefault()}
                  className="border-2 border-dashed border-[#27272A] hover:border-[#FF5C1A] transition-colors p-8 text-center cursor-pointer"
                  onClick={() => importFileRef.current?.click()}
                >
                  <Upload size={32} className="mx-auto text-[#888880] mb-3" />
                  <p className="text-sm text-[#888880]">Drag & drop Excel file or click to upload</p>
                  <p className="text-[10px] text-[#888880] font-mono mt-1">.xlsx only (max 10MB)</p>
                  <input ref={importFileRef} type="file" accept=".xlsx,.xls" onChange={handleImportFile} className="hidden" data-testid="import-file-input" />
                </div>

                {importLoading && (
                  <div className="text-center py-4 text-[#888880] font-mono text-sm">Parsing file...</div>
                )}

                {importPreview && (
                  <div data-testid="import-preview">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-sm text-[#F5F2E8]">{importPreview.total_rows} rows found</span>
                      {importPreview.error_count > 0 && (
                        <span className="flex items-center gap-1 font-mono text-xs text-[#E74C3C]">
                          <AlertTriangle size={12} /> {importPreview.error_count} rows with issues
                        </span>
                      )}
                    </div>
                    <div className="max-h-60 overflow-y-auto border border-[#27272A]">
                      <table className="w-full text-xs">
                        <thead className="bg-[#0A0A08] sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-mono text-[#888880] tracking-widest uppercase">Name</th>
                            <th className="px-3 py-2 text-left font-mono text-[#888880] tracking-widest uppercase">Phone</th>
                            <th className="px-3 py-2 text-left font-mono text-[#888880] tracking-widest uppercase">Aadhaar</th>
                            <th className="px-3 py-2 text-left font-mono text-[#888880] tracking-widest uppercase">Role</th>
                            <th className="px-3 py-2 text-left font-mono text-[#888880] tracking-widest uppercase">Wage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.workers?.map((w, i) => (
                            <tr key={i} className={`border-t border-[#27272A] ${w.has_errors ? 'bg-[#E74C3C]/5' : ''}`}>
                              <td className={`px-3 py-2 ${w.error_fields?.includes('name') ? 'text-[#E74C3C]' : ''}`}>{w.name || <span className="text-[#E74C3C]">Missing</span>}</td>
                              <td className={`px-3 py-2 font-mono ${w.error_fields?.includes('phone') ? 'text-[#E74C3C]' : 'text-[#888880]'}`}>{w.phone || <span className="text-[#E74C3C]">Missing</span>}</td>
                              <td className={`px-3 py-2 font-mono ${w.error_fields?.includes('aadhaar_number') ? 'text-[#E74C3C]' : 'text-[#888880]'}`}>{w.aadhaar_number || <span className="text-[#E74C3C]">Missing</span>}</td>
                              <td className="px-3 py-2">{w.role}</td>
                              <td className="px-3 py-2 font-mono text-[#FFB800]">{w.daily_wage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button data-testid="confirm-import-btn" onClick={confirmImport} disabled={confirmLoading} className="w-full mt-4 bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors disabled:opacity-50">
                      {confirmLoading ? "Importing..." : `Confirm Import (${importPreview.total_rows} workers)`}
                    </button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Worker */}
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditId(null); }}>
            <DialogTrigger asChild>
              <button data-testid="add-worker-btn" className="bg-[#FF5C1A] text-[#0A0A08] px-5 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-none">
                <Plus size={16} /> Add Worker
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-[2px]">{editId ? "EDIT WORKER" : "ADD WORKER"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Name</label>
                  <input data-testid="worker-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} placeholder="Rajesh Kumar" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Role</label>
                    <select data-testid="worker-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                      {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Daily Wage</label>
                    <input data-testid="worker-wage-input" type="number" value={form.daily_wage} onChange={(e) => setForm({ ...form, daily_wage: e.target.value })} className={inputCls} placeholder="800" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Phone</label>
                    <input data-testid="worker-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="9876543210" />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Advance</label>
                    <input data-testid="worker-advance-input" type="number" value={form.advance} onChange={(e) => setForm({ ...form, advance: e.target.value })} className={inputCls} placeholder="5000" />
                  </div>
                </div>

                <div className="pt-2 border-t border-[#27272A]">
                  <h4 className="font-mono text-[10px] text-[#FF5C1A] tracking-[2px] uppercase mb-3">KYC & BANK DETAILS</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="flex items-center gap-1 font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">
                        <Fingerprint size={10} /> Aadhaar Number
                      </label>
                      <input data-testid="worker-aadhaar-input" value={form.aadhaar_number} onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value })} className={`${inputCls} font-mono`} placeholder="1234-5678-9012" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="flex items-center gap-1 font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">
                          <CreditCard size={10} /> Bank Account
                        </label>
                        <input data-testid="worker-bank-input" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} className={`${inputCls} font-mono`} placeholder="12345678901234" />
                      </div>
                      <div>
                        <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">IFSC Code</label>
                        <input data-testid="worker-ifsc-input" value={form.ifsc_code} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value })} className={`${inputCls} font-mono uppercase`} placeholder="SBIN0001234" />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Site Assigned</label>
                  <input data-testid="worker-site-input" value={form.site_assigned} onChange={(e) => setForm({ ...form, site_assigned: e.target.value })} className={inputCls} placeholder="Lucknow-Kanpur Highway" />
                </div>
                <button data-testid="submit-worker-btn" type="submit" className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors">
                  {editId ? "Update Worker" : "Add Worker"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#111110] border border-[#27272A] p-4 text-center">
          <div className="font-heading text-3xl text-[#FF5C1A]">{workerList.length}</div>
          <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">Total</div>
        </div>
        <div className="bg-[#111110] border border-[#27272A] p-4 text-center">
          <div className="font-heading text-3xl text-[#2ECC71]">{presentCount}</div>
          <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">Present</div>
        </div>
        <div className="bg-[#111110] border border-[#27272A] p-4 text-center">
          <div className="font-heading text-3xl text-[#FFB800]">{workerList.reduce((s, w) => s + (w.daily_wage || 0), 0).toLocaleString("en-IN")}</div>
          <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">Daily Cost</div>
        </div>
        <div className="bg-[#111110] border border-[#27272A] p-4 text-center">
          <div className="font-heading text-3xl text-[#E74C3C]">{workerList.reduce((s, w) => s + (w.advance || 0), 0).toLocaleString("en-IN")}</div>
          <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">Total Advance</div>
        </div>
      </div>

      {/* Worker List */}
      {loading ? (
        <div className="text-center py-12 text-[#888880] font-mono text-sm">Loading...</div>
      ) : workerList.length === 0 ? (
        <div className="text-center py-16 bg-[#111110] border border-[#27272A]">
          <div className="text-[#888880] font-mono text-sm">No workers added yet. Add individually or import from Excel.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {workerList.map((w) => {
            const isPresent = w.attendance?.[today] === true;
            return (
              <div key={w.id} data-testid={`worker-item-${w.id}`} className="bg-[#111110] border border-[#27272A] p-4 group hover:border-[#FF5C1A]/30 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <Switch
                      data-testid={`attendance-toggle-${w.id}`}
                      checked={isPresent}
                      onCheckedChange={(checked) => handleAttendance(w.id, checked)}
                      className="data-[state=checked]:bg-[#2ECC71]"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {w.name}
                        <span className="font-mono text-[10px] px-2 py-0.5 bg-[#FF5C1A]/10 text-[#FF5C1A] border border-[#FF5C1A]/20">{w.role}</span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#888880]">
                        {w.site_assigned && <span className="flex items-center gap-1"><MapPin size={10} /> {w.site_assigned}</span>}
                        {w.phone && <span className="flex items-center gap-1"><Phone size={10} /> {w.phone}</span>}
                        {w.aadhaar_number && <span className="flex items-center gap-1 font-mono"><Fingerprint size={10} /> {w.aadhaar_number}</span>}
                        {w.bank_account && <span className="flex items-center gap-1 font-mono"><CreditCard size={10} /> ****{w.bank_account.slice(-4)}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6 text-sm">
                    <div className="text-center">
                      <div className="font-mono text-[#FFB800]">{w.daily_wage}</div>
                      <div className="font-mono text-[9px] text-[#888880] tracking-widest uppercase">Wage/Day</div>
                    </div>
                    <div className="text-center">
                      <div className="font-mono text-[#E74C3C]">{w.advance}</div>
                      <div className="font-mono text-[9px] text-[#888880] tracking-widest uppercase">Advance</div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button data-testid={`edit-worker-${w.id}`} onClick={() => openEdit(w)} className="p-1.5 text-[#888880] hover:text-[#FF5C1A]"><Pencil size={14} /></button>
                      <button data-testid={`delete-worker-${w.id}`} onClick={() => handleDelete(w.id)} className="p-1.5 text-[#888880] hover:text-[#E74C3C]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
